import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { computeInitialEditText } from "../utils";

import type { JsonValue, SmartCell } from "@/queries/smart-tables/types";
import type { CellContentType } from "@/routes/smart-table/utils/contentType";

const DIALOG_AUTOSAVE_DELAY_MS = 900;

interface CommitCallbacks {
  onSuccess: () => void;
  onError: () => void;
}

interface UseDialogEditPipelineParams<TSnapshot> {
  canEdit: boolean;
  cell: SmartCell | undefined;
  tableId: string | undefined;
  sheetId: string | undefined;
  sheetDataStore: {
    getCellSnapshot: (cellId: string) => TSnapshot;
    updateCell: (cellId: string, displayValue: string, value?: SmartCell["value"]) => void;
  } | null;
  displayType: CellContentType;
  displayValue: string | null;
  isOpen: boolean;
  rowIndex: number;
  displayColumnId: string;
  onCloseDialog: () => void;
  commitToServer: (
    args: {
      tableId: string;
      sheetId: string;
      cellId: string;
      display_value: string;
      value?: JsonValue;
    },
    snapshot: TSnapshot,
    callbacks: CommitCallbacks,
  ) => void;
}

export function useDialogEditPipeline<TSnapshot>({
  canEdit,
  cell,
  tableId,
  sheetId,
  sheetDataStore,
  displayType,
  displayValue,
  isOpen,
  rowIndex,
  displayColumnId,
  onCloseDialog,
  commitToServer,
}: UseDialogEditPipelineParams<TSnapshot>) {
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const editValueRef = useRef("");
  const initializedEditValueRef = useRef("");
  const prevCellKeyRef = useRef("");
  const prevCanEditRef = useRef(false);
  const prevDisplayTypeRef = useRef<CellContentType | null>(null);
  const prevDisplayValueRef = useRef<string | null>(null);
  const pendingAutosaveTimeoutRef = useRef<number | null>(null);
  const inFlightRef = useRef<{ cellId: string; value: string } | null>(null);
  const queuedRef = useRef<{ cellId: string; value: string; displayType: CellContentType } | null>(
    null,
  );
  const activeCellIdRef = useRef<string | undefined>(cell?.id);
  const isOpenRef = useRef(isOpen);
  const commitEditRef = useRef<() => void>(() => undefined);
  const submitSaveRef = useRef<
    (targetCellId: string, nextDisplayValue: string, saveDisplayType: CellContentType) => void
  >(() => undefined);

  isOpenRef.current = isOpen;
  activeCellIdRef.current = cell?.id;

  const clearPendingAutosave = useCallback(() => {
    if (pendingAutosaveTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(pendingAutosaveTimeoutRef.current);
    pendingAutosaveTimeoutRef.current = null;
  }, []);

  const resetEditState = useCallback(() => {
    setEditValue("");
    editValueRef.current = "";
    initializedEditValueRef.current = "";
    inFlightRef.current = null;
    queuedRef.current = null;
    setIsSaving(false);
    clearPendingAutosave();
  }, [clearPendingAutosave]);

  const finishSave = useCallback(
    (targetCellId: string) => {
      if (inFlightRef.current !== null || queuedRef.current !== null) {
        return;
      }

      setIsSaving(false);

      if (!isOpenRef.current && activeCellIdRef.current === targetCellId) {
        resetEditState();
      }
    },
    [resetEditState],
  );

  const submitSave = useCallback(
    (targetCellId: string, nextDisplayValue: string, saveDisplayType: CellContentType) => {
      if (!tableId || !sheetId || !sheetDataStore) {
        return;
      }

      let jsonValue: JsonValue | undefined;
      if (saveDisplayType === "json") {
        try {
          jsonValue = JSON.parse(nextDisplayValue) as JsonValue;
        } catch {
          // Save as plain string
        }
      }

      const snapshot = sheetDataStore.getCellSnapshot(targetCellId);
      inFlightRef.current = { cellId: targetCellId, value: nextDisplayValue };
      if (queuedRef.current?.cellId === targetCellId) {
        queuedRef.current = null;
      }
      setIsSaving(true);
      sheetDataStore.updateCell(targetCellId, nextDisplayValue, jsonValue ?? undefined);

      commitToServer(
        {
          tableId,
          sheetId,
          cellId: targetCellId,
          display_value: nextDisplayValue,
          ...(jsonValue !== undefined ? { value: jsonValue } : {}),
        },
        snapshot,
        {
          onSuccess: () => {
            const stillEditingThisCell = activeCellIdRef.current === targetCellId;
            if (stillEditingThisCell && editValueRef.current === nextDisplayValue) {
              initializedEditValueRef.current = nextDisplayValue;
            }

            if (
              inFlightRef.current?.cellId === targetCellId &&
              inFlightRef.current.value === nextDisplayValue
            ) {
              inFlightRef.current = null;
            }

            const queued = queuedRef.current;
            if (queued?.cellId === targetCellId) {
              queuedRef.current = null;
              submitSaveRef.current(targetCellId, queued.value, queued.displayType);
              return;
            }

            if (stillEditingThisCell && editValueRef.current !== initializedEditValueRef.current) {
              commitEditRef.current();
              return;
            }

            finishSave(targetCellId);
          },
          onError: () => {
            if (
              inFlightRef.current?.cellId === targetCellId &&
              inFlightRef.current.value === nextDisplayValue
            ) {
              inFlightRef.current = null;
            }

            const queued = queuedRef.current;
            if (queued?.cellId === targetCellId) {
              queuedRef.current = null;
              if (queued.value !== nextDisplayValue) {
                submitSaveRef.current(targetCellId, queued.value, queued.displayType);
                return;
              }
            }

            finishSave(targetCellId);
          },
        },
      );
    },
    [tableId, sheetId, sheetDataStore, commitToServer, finishSave],
  );

  submitSaveRef.current = submitSave;

  const commitEdit = useCallback(() => {
    if (!cell || !tableId || !sheetId || !sheetDataStore) {
      return;
    }

    clearPendingAutosave();

    const nextDisplayValue = editValueRef.current;
    if (nextDisplayValue === initializedEditValueRef.current) {
      if (queuedRef.current?.cellId === cell.id) {
        queuedRef.current = null;
      }
      return;
    }

    if (inFlightRef.current?.cellId === cell.id) {
      queuedRef.current = { cellId: cell.id, value: nextDisplayValue, displayType };
      return;
    }

    submitSave(cell.id, nextDisplayValue, displayType);
  }, [cell, tableId, sheetId, sheetDataStore, displayType, clearPendingAutosave, submitSave]);

  commitEditRef.current = commitEdit;

  useLayoutEffect(() => {
    if (!isOpen) {
      prevCellKeyRef.current = "";
      prevCanEditRef.current = false;
      prevDisplayTypeRef.current = null;
      prevDisplayValueRef.current = null;
      if (inFlightRef.current === null && queuedRef.current === null) {
        resetEditState();
      }
      return;
    }

    const cellKey = `${rowIndex}-${displayColumnId}-${cell?.id ?? ""}`;
    const isNewCell = cellKey !== prevCellKeyRef.current;
    const canEditJustBecameTrue = canEdit && !prevCanEditRef.current;
    const displayTypeChanged =
      prevDisplayTypeRef.current !== null && prevDisplayTypeRef.current !== displayType;
    const displayValueChanged = prevDisplayValueRef.current !== displayValue;
    const hasUnsavedEdit = editValueRef.current !== initializedEditValueRef.current;

    prevCellKeyRef.current = cellKey;
    prevCanEditRef.current = canEdit;
    prevDisplayTypeRef.current = displayType;
    prevDisplayValueRef.current = displayValue;

    if (!canEdit) {
      if (isNewCell || canEditJustBecameTrue) {
        setEditValue("");
        editValueRef.current = "";
        initializedEditValueRef.current = "";
      }
      return;
    }

    if (!isNewCell && !canEditJustBecameTrue && !displayTypeChanged && !displayValueChanged) {
      return;
    }

    if ((displayTypeChanged || displayValueChanged) && hasUnsavedEdit && !isNewCell) {
      return;
    }

    const initial = computeInitialEditText(displayValue, displayType);
    setEditValue(initial);
    editValueRef.current = initial;
    initializedEditValueRef.current = initial;
  }, [
    isOpen,
    rowIndex,
    displayColumnId,
    cell?.id,
    canEdit,
    displayValue,
    displayType,
    resetEditState,
  ]);

  const scheduleAutosave = useCallback(() => {
    clearPendingAutosave();

    if (!canEdit) {
      return;
    }

    pendingAutosaveTimeoutRef.current = window.setTimeout(() => {
      pendingAutosaveTimeoutRef.current = null;
      commitEditRef.current();
    }, DIALOG_AUTOSAVE_DELAY_MS);
  }, [canEdit, clearPendingAutosave]);

  useEffect(() => clearPendingAutosave, [clearPendingAutosave]);

  const handleEditChange = useCallback(
    (value: string) => {
      setEditValue(value);
      editValueRef.current = value;
      scheduleAutosave();
    },
    [scheduleAutosave],
  );

  const handleClose = useCallback(() => {
    if (canEdit) {
      commitEdit();
    } else {
      clearPendingAutosave();
    }

    onCloseDialog();
  }, [canEdit, clearPendingAutosave, commitEdit, onCloseDialog]);

  return {
    editValue,
    editValueRef,
    handleEditChange,
    handleClose,
    commitEdit,
    isSaving,
  };
}
